/**
 * BuilderApiClient
 *
 * Client for communicating with the Builder Service API.
 * Handles publishing placements to GCS.
 *
 * @module placements/services
 */

import type {
  WebABRoutingConfig,
  WebPlacementConfig,
} from '@modules/webplacements/types/webplacements.types';
import type { ProjectAnalyticsSettings } from '@modules/analytics-settings/types/analytics.types';
import type { FlowDocument } from '@segmently/schema-models/v2';
import type { PublishGateFinding } from '@modules/shared-types/src/launchActivation.types';

/**
 * Parameters for web publish request
 */
export interface PublishWebParams {
  /** Web alias for URL (e.g., 'summer-promo' → /w/summer-promo) */
  alias: string;
  /** Funnel (onboarding) ID */
  funnelId: string;
  /** Firestore OnboardingVersion document ID */
  versionId: string;
  /** V2 FlowDocument for GCS upload */
  config: FlowDocument;
  /** Project ID */
  projectId: string;
  /** WebPlacement config for baking (parameters, SEO, analytics, etc.) */
  webPlacementConfig?: WebPlacementConfig;
  /**
   * Project-level analytics settings (Amplitude, GA4, GTM, etc.)
   * These are configured on the Analytics Settings page and apply to ALL web placements.
   */
  projectAnalytics?: ProjectAnalyticsSettings;
}

/**
 * Structured Builder Service publish failure.
 *
 * Carries the machine-readable `code` (e.g. `analytics_variable_privacy_review_required`)
 * and the healthcheck issue list so UI surfaces can react to a specific failure
 * instead of showing the raw response body.
 */
export class PublishWebError extends Error {
  constructor(
    message: string,
    /** HTTP status returned by Builder Service */
    public readonly status: number,
    /** Stable machine-readable error code from the response body */
    public readonly code?: string,
    /** Healthcheck issues attached to the failure (evidence for review UIs) */
    public readonly issues?: unknown[],
  ) {
    super(message);
    this.name = 'PublishWebError';
  }
}

/**
 * Parameters for the side-effect-free publish privacy healthcheck
 * (`POST /api/publish/web/healthcheck`). Same body shape as publishWeb minus
 * upload-only fields.
 */
export interface PublishWebHealthcheckParams {
  /** V2 FlowDocument to evaluate (required by the endpoint) */
  config: FlowDocument;
  /** Project ID — lets the server load analytics settings from Firestore */
  projectId?: string;
  /** Optional analytics settings override (server loads from Firestore when absent) */
  projectAnalytics?: ProjectAnalyticsSettings;
}

/**
 * Structured healthcheck evaluation. Same body the 422 publish gate returns,
 * but obtained without attempting an upload.
 */
export interface PublishWebHealthcheckResult {
  success: boolean;
  /** e.g. analytics_variable_privacy_review_required / _passed */
  code?: string;
  error?: string;
  analyticsEnabled?: boolean;
  healthcheck?: {
    status?: string;
    errors?: number;
    warnings?: number;
    issues?: unknown[];
  };
  /** Tier-2 publish-gate findings (contracts §2; Plan A server). Absent on older servers. */
  findings?: PublishGateFinding[];
}

/**
 * Result of web publish request
 */
export interface PublishWebResult {
  /** Whether publish was successful */
  success: boolean;
  /** GCS path where config was uploaded */
  gcsPath: string;
  /** Web URL path (e.g., /w/summer-promo) */
  webUrl: string;
  /** The alias used */
  alias: string;
  /** Web-shell version baked into the published config */
  webShellVersion?: string;
}

/**
 * Parameters for A/B test routing config upload
 */
export interface UploadABRoutingConfigParams {
  /** A/B test alias */
  alias: string;
  /** Routing config with salt and variants */
  routingConfig: WebABRoutingConfig;
  /** Project ID */
  projectId: string;
  /** Hash currently expected at the stable alias; null means create-only. */
  expectedRoutingHash?: string | null;
}

/**
 * Result of A/B routing config upload
 */
export interface UploadABRoutingConfigResult {
  /** Whether upload was successful */
  success: boolean;
  /** GCS path where config was uploaded */
  gcsPath?: string;
  /** Error message if failed */
  error?: string;
  /** Stable bounded server error code (for example routing hash conflict). */
  errorCode?: string;
}

export interface DeleteABRoutingConfigParams {
  alias: string;
  projectId: string;
  /** Delete only if this exact candidate hash is still served. */
  expectedRoutingHash: string;
}

/**
 * Client interface for Builder Service API
 *
 * This shared interface intentionally exposes only Web Publishing operations.
 */
export interface BuilderApiClient {
  /**
   * Publish a web funnel configuration to GCS
   *
   * @param params - Web publish parameters
   * @returns Web publish result with GCS path and URL
   */
  publishWeb(params: PublishWebParams): Promise<PublishWebResult>;

  /**
   * Evaluate the publish privacy gate WITHOUT publishing.
   *
   * Returns the structured gate body for any HTTP status that carries one
   * (200 passed, 422 review required, 503 settings unavailable); throws only
   * on network failures or non-JSON responses.
   */
  publishWebHealthcheck(params: PublishWebHealthcheckParams): Promise<PublishWebHealthcheckResult>;

  /**
   * Upload A/B test routing config to GCS
   *
   * Uploads a lightweight JSON config (~200 bytes) to web-ab/{alias}.json
   * that the Edge Gateway uses to resolve variants.
   *
   * @param params - Upload parameters
   * @returns Upload result with GCS path
   */
  uploadABRoutingConfig(params: UploadABRoutingConfigParams): Promise<UploadABRoutingConfigResult>;

  /** Conditionally remove a first-publish artifact during compensation. */
  deleteABRoutingConfig(params: DeleteABRoutingConfigParams): Promise<UploadABRoutingConfigResult>;
}
